import { v4 } from 'uuid';

import { type IWorkspaceMembershipRepository } from '../../ports/workspace-membership.repository';

export interface ListWorkspaceMembersInput {
  workspaceId: string;
  limit: number;
  searchTerm?: string;
  lastMembershipId?: string;
}

export interface ListedWorkspaceMember {
  userId: string;
  username: string;
  userVerified: boolean;
  membershipId: string;
}

export class ListWorkspaceMembersQueryError extends Error {
  constructor(message = 'Failed to list workspace members', public readonly cause?: unknown) {
    super(message);
    this.name = 'ListWorkspaceMembersQueryError';
    Object.setPrototypeOf(this, ListWorkspaceMembersQueryError.prototype);
  }
}

export interface IListWorkspaceMembersQuery {
  /**
   * List members of a workspace, mapped to the fields consumed by the
   * WorkspaceMembers UI store. When the underlying record lacks user data, the
   * row is still emitted with a synthetic id and a placeholder username so the
   * data inconsistency surfaces in the UI.
   * @throws ListWorkspaceMembersQueryError when the underlying read fails.
   */
  execute(input: ListWorkspaceMembersInput): Promise<ListedWorkspaceMember[]>;
}

const BROKEN_USERNAME = '????';

export class ListWorkspaceMembersQuery implements IListWorkspaceMembersQuery {
  constructor(private readonly repository: IWorkspaceMembershipRepository) {}

  public async execute(input: ListWorkspaceMembersInput): Promise<ListedWorkspaceMember[]> {
    try {
      const rows = await this.repository.list(
        input.workspaceId,
        input.limit,
        input.searchTerm,
        input.lastMembershipId
      );
      return rows.map((row) => ({
        userId: row.userId ?? v4(),
        username: row.username ?? BROKEN_USERNAME,
        userVerified: row.userVerified ?? true,
        membershipId: row.membershipId,
      }));
    } catch (error) {
      throw new ListWorkspaceMembersQueryError('Failed to list workspace members', error);
    }
  }
}
